import React, { useEffect, useState } from 'react';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Package, Calendar, IndianRupee, Building2, Search, Printer, Download, Eye, FileText, Upload, X } from 'lucide-react';
import { useAuth } from '../AuthContext';
import AssetDetailDialog from '../components/AssetDetailDialog';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ASSET_TYPES = [
  { value: 'Electronic', label: 'Electronic' },
  { value: 'Machinery', label: 'Machinery' },
  { value: 'Furniture', label: 'Furniture' },
  { value: 'Land', label: 'Land' },
  { value: 'Vehicle', label: 'Vehicle' },
  { value: 'Building', label: 'Building' },
  { value: 'Other', label: 'Other (specify)' },
];

const ASSET_CATEGORIES = [
  'Office Equipment',
  'IT Equipment',
  'Furniture & Fixtures',
  'Building',
  'Land',
  'Vehicle',
  'Machinery',
  'Other',
];

const ASSET_CONDITIONS = ['Good', 'Fair', 'Poor', 'Damaged', 'Under Repair'];
const ASSET_STATUSES = ['Active', 'Disposed', 'Under Repair', 'Transferred'];

const Assets = () => {
  const { isAdmin, isAdminOnly, user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Detail viewer
  const [detailAsset, setDetailAsset] = useState(null);
  const [detailBill, setDetailBill] = useState(null);

  // Bill upload (in form)
  const [billFile, setBillFile] = useState(null);
  const [billDataUrl, setBillDataUrl] = useState('');

  const [formData, setFormData] = useState({
    asset_name: '',
    asset_type: '',
    asset_type_other: '',
    category: '',
    purchase_date: '',
    purchase_price: 0,
    quantity: 1,
    life_expectancy_years: 5,
    depreciation_rate: '',
    location: '',
    condition: 'Good',
    remarks: '',
  });

  useEffect(() => {
    fetchData();
  }, [filterBranch]);

  // Real-time auto-refresh
  useAutoRefresh(() => fetchData());

  const fetchData = async () => {
    try {
      const [assetsRes, branchesRes] = await Promise.all([
        api.get('/assets', { params: filterBranch !== 'all' ? { branch_id: filterBranch } : {} }),
        api.get('/branches'),
      ]);
      setAssets(assetsRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        quantity: Math.max(1, parseInt(formData.quantity, 10) || 1),
        life_expectancy_years: parseInt(formData.life_expectancy_years) || 5,
        depreciation_rate: formData.depreciation_rate ? parseFloat(formData.depreciation_rate) : null,
        gst_amount: formData.gst_amount ? parseFloat(formData.gst_amount) : null,
        purchase_date: new Date(formData.purchase_date).toISOString(),
      };

      // Attach bill (only if a new file was chosen)
      if (billDataUrl && billFile) {
        payload.bill_document = billDataUrl;
        payload.bill_filename = billFile.name;
        payload.bill_mime_type = billFile.type || 'application/octet-stream';
      }

      if (editingAsset) {
        await api.put(`/assets/${editingAsset.id}`, payload);
        toast.success('Asset updated successfully');
      } else {
        await api.post('/assets', payload);
        toast.success('Asset created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      asset_name: asset.asset_name || '',
      asset_type: asset.asset_type || '',
      asset_type_other: asset.asset_type_other || '',
      category: asset.category || '',
      purchase_date: asset.purchase_date ? asset.purchase_date.split('T')[0] : '',
      purchase_price: asset.purchase_price || 0,
      quantity: asset.quantity ?? 1,
      life_expectancy_years: asset.life_expectancy_years || 5,
      depreciation_rate: asset.depreciation_rate || '',
      location: asset.location || '',
      condition: asset.condition || 'Good',
      remarks: asset.remarks || '',
      vendor_name: asset.vendor_name || '',
      bill_number: asset.bill_number || '',
      bill_date: asset.bill_date || '',
      gst_number: asset.gst_number || '',
      gst_amount: asset.gst_amount ?? '',
    });
    setBillFile(null);
    setBillDataUrl('');
    setDialogOpen(true);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await api.delete(`/assets/${assetId}`);
      toast.success('Asset deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const resetForm = () => {
    setEditingAsset(null);
    setFormData({
      asset_name: '',
      asset_type: '',
      asset_type_other: '',
      category: '',
      purchase_date: '',
      purchase_price: 0,
      quantity: 1,
      life_expectancy_years: 5,
      depreciation_rate: '',
      location: '',
      condition: 'Good',
      remarks: '',
      vendor_name: '',
      bill_number: '',
      bill_date: '',
      gst_number: '',
      gst_amount: '',
    });
    setBillFile(null);
    setBillDataUrl('');
  };

  // ==== Bill file picker ====
  const handleBillFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { // 10 MB
      toast.error('Bill file too large (max 10 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setBillFile(f);
      setBillDataUrl(reader.result);
    };
    reader.readAsDataURL(f);
  };

  // ==== Open detail dialog & fetch bill (if any) ====
  const openDetail = async (asset) => {
    setDetailAsset(asset);
    setDetailBill(null);
    if (asset.has_bill) {
      try {
        const r = await api.get(`/assets/${asset.id}/bill`);
        setDetailBill(r.data);
      } catch (err) {
        // Bill endpoint may 404 if not uploaded
      }
    }
  };

  const downloadBill = () => {
    if (!detailBill?.bill_document) return;
    const a = document.createElement('a');
    a.href = detailBill.bill_document;
    a.download = detailBill.bill_filename || 'asset_bill';
    a.click();
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = 
      asset.asset_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.asset_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.category?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || asset.asset_type === filterType;
    return matchesSearch && matchesType;
  });

  // Calculate summary — multiply unit price by quantity for line totals.
  const lineTotal = (a) => (a.total_purchase_value ?? ((a.purchase_price || 0) * (a.quantity ?? 1)));
  const summary = {
    totalAssets: filteredAssets.length,
    totalValue: filteredAssets.reduce((sum, a) => sum + lineTotal(a), 0),
    currentValue: filteredAssets.reduce((sum, a) => sum + (a.current_value ?? lineTotal(a)), 0),
    depreciation: 0,
  };
  summary.depreciation = Math.max(0, summary.totalValue - summary.currentValue);

  // Print function
  const handlePrint = () => {
    const printContent = document.getElementById('asset-register-print');
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const html = `
      <html>
        <head>
          <title>Asset Register - WWAGS MIS</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 11px; padding: 20px; }
            h1 { color: #047857; font-size: 18px; margin-bottom: 5px; }
            h2 { font-size: 14px; color: #333; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #047857; color: white; font-size: 10px; }
            td { font-size: 10px; }
            .summary { margin-bottom: 15px; }
            .summary-item { display: inline-block; margin-right: 30px; }
            .text-right { text-align: right; }
            .footer { margin-top: 20px; font-size: 9px; color: #666; text-align: center; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="footer">
            MIS Developed by Institute of Skill Development and Entrepreneurship (ISDE), Manipur<br>
            Printed on: ${new Date().toLocaleString('en-IN')}
          </div>
        </body>
      </html>
    `;
    writePrintDoc(printWindow.document, html);
    printWindow.print();
  };

  // Download PDF
  const downloadPDF = () => {
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(18);
    doc.setTextColor(4, 120, 87);
    doc.text('WWAGS MIS Portal - Asset Register', 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 14, 22);
    doc.text(`Total Assets: ${summary.totalAssets} | Total Value: Rs. ${summary.totalValue.toLocaleString('en-IN')} | Current Value: Rs. ${summary.currentValue.toLocaleString('en-IN')}`, 14, 28);
    
    const tableData = filteredAssets.map(asset => [
      asset.asset_code,
      asset.asset_name,
      asset.asset_type === 'Other' ? asset.asset_type_other : asset.asset_type,
      asset.category,
      asset.quantity ?? 1,
      new Date(asset.purchase_date).toLocaleDateString('en-IN'),
      `Rs. ${(asset.purchase_price || 0).toLocaleString('en-IN')}`,
      `Rs. ${lineTotal(asset).toLocaleString('en-IN')}`,
      `${asset.life_expectancy_years} yrs`,
      `Rs. ${(asset.current_value ?? lineTotal(asset)).toLocaleString('en-IN')}`,
      asset.condition,
      asset.branch_name
    ]);
    
    autoTable(doc, {
      startY: 34,
      head: [['Asset Code', 'Asset Name', 'Type', 'Category', 'Qty', 'Purchase Date', 'Unit Price', 'Total Value', 'Life', 'Current Value', 'Condition', 'Branch']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [4, 120, 87], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
    });
    
    doc.save(`Asset_Register_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Asset Register PDF downloaded');
  };

  // Download Excel
  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    
    const data = [
      ['WWAGS MIS Portal - Asset Register'],
      [`Generated on: ${new Date().toLocaleDateString('en-IN')}`],
      [],
      ['Asset Code', 'Asset Name', 'Type', 'Category', 'Qty', 'Purchase Date', 'Unit Price', 'Total Value (Qty×Unit)', 'Life (Years)', 'Depreciation Rate', 'Current Value', 'Total Depreciation', 'Condition', 'Status', 'Location', 'Branch', 'Remarks'],
      ...filteredAssets.map(asset => [
        asset.asset_code,
        asset.asset_name,
        asset.asset_type === 'Other' ? asset.asset_type_other : asset.asset_type,
        asset.category,
        asset.quantity ?? 1,
        new Date(asset.purchase_date).toLocaleDateString('en-IN'),
        asset.purchase_price,
        lineTotal(asset),
        asset.life_expectancy_years,
        asset.depreciation_rate ? `${asset.depreciation_rate}%` : '',
        asset.current_value ?? lineTotal(asset),
        asset.total_depreciation ?? Math.max(0, lineTotal(asset) - (asset.current_value ?? lineTotal(asset))),
        asset.condition,
        asset.status,
        asset.location || '',
        asset.branch_name,
        asset.remarks || ''
      ])
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 6 }, { wch: 12 },
      { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
      { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 18 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Asset Register');
    
    XLSX.writeFile(wb, `Asset_Register_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Asset Register Excel downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Asset Management</h1>
          <p className="text-sm text-slate-600">Manage branch assets and track depreciation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} data-testid="print-assets-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button variant="outline" onClick={downloadPDF} data-testid="download-assets-pdf">
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button variant="outline" onClick={downloadExcel} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="download-assets-excel">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-asset-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="asset_name">Asset Name *</Label>
                    <Input
                      id="asset_name"
                      value={formData.asset_name}
                      onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                      required
                      placeholder="e.g., Dell Laptop, Office Chair"
                      data-testid="asset-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="asset_type">Asset Type *</Label>
                    <Select
                      value={formData.asset_type}
                      onValueChange={(value) => setFormData({ ...formData, asset_type: value })}
                    >
                      <SelectTrigger data-testid="asset-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSET_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.asset_type === 'Other' && (
                    <div>
                      <Label htmlFor="asset_type_other">Specify Type *</Label>
                      <Input
                        id="asset_type_other"
                        value={formData.asset_type_other}
                        onChange={(e) => setFormData({ ...formData, asset_type_other: e.target.value })}
                        required
                        placeholder="Enter asset type"
                        data-testid="asset-type-other-input"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="asset-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSET_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="purchase_date">Date of Purchase *</Label>
                    <Input
                      id="purchase_date"
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                      required
                      data-testid="asset-purchase-date-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="purchase_price">Purchase Price (Rs.) *</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      required
                      data-testid="asset-price-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      step="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                      placeholder="e.g., 2"
                      data-testid="asset-quantity-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="life_expectancy_years">Life Expectancy (Years) *</Label>
                    <Input
                      id="life_expectancy_years"
                      type="number"
                      min="1"
                      value={formData.life_expectancy_years}
                      onChange={(e) => setFormData({ ...formData, life_expectancy_years: e.target.value })}
                      required
                      data-testid="asset-life-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="depreciation_rate">Depreciation Rate (% per year)</Label>
                    <Input
                      id="depreciation_rate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.depreciation_rate}
                      onChange={(e) => setFormData({ ...formData, depreciation_rate: e.target.value })}
                      placeholder="Auto-calculated if empty"
                      data-testid="asset-depreciation-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="condition">Condition</Label>
                    <Select
                      value={formData.condition}
                      onValueChange={(value) => setFormData({ ...formData, condition: value })}
                    >
                      <SelectTrigger data-testid="asset-condition-select">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSET_CONDITIONS.map(cond => (
                          <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Office Room 1"
                      data-testid="asset-location-input"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label htmlFor="remarks">Remarks</Label>
                    <Input
                      id="remarks"
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                      placeholder="Additional notes"
                      data-testid="asset-remarks-input"
                    />
                  </div>
                </div>

                {/* === Purchase Bill / GST Section === */}
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-amber-700" />
                    <h3 className="text-sm font-semibold text-amber-800">Purchase Bill & GST Details</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Vendor / Supplier Name</Label>
                      <Input value={formData.vendor_name} onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })} placeholder="e.g. Reliance Digital, Local Furniture Mart" data-testid="asset-vendor-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Bill / Invoice No.</Label>
                      <Input value={formData.bill_number} onChange={(e) => setFormData({ ...formData, bill_number: e.target.value })} placeholder="e.g. INV-2026/0042" data-testid="asset-billno-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Bill Date</Label>
                      <Input type="date" value={formData.bill_date} onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })} data-testid="asset-billdate-input" />
                    </div>
                    <div>
                      <Label className="text-xs">GST No. (if available)</Label>
                      <Input value={formData.gst_number} onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })} placeholder="e.g. 14ABCDE1234F1Z5" maxLength={15} data-testid="asset-gst-input" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">GST Amount (Rs.) — if charged separately</Label>
                      <Input type="number" min="0" step="0.01" value={formData.gst_amount} onChange={(e) => setFormData({ ...formData, gst_amount: e.target.value })} placeholder="e.g. 1800" data-testid="asset-gstamt-input" />
                    </div>
                  </div>

                  <div className="space-y-2 pt-2">
                    <Label className="text-xs">Original Purchase Bill (PDF / JPG / PNG, max 10 MB)</Label>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-white border border-amber-300 rounded-md text-xs font-medium text-amber-700 hover:bg-amber-100 transition" data-testid="asset-bill-upload-label">
                        <Upload className="w-3.5 h-3.5" />
                        {billFile ? 'Change Bill' : (editingAsset?.has_bill ? 'Replace Bill' : 'Upload Bill')}
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleBillFileSelect} className="hidden" data-testid="asset-bill-file-input" />
                      </label>
                      {billFile && (
                        <div className="text-xs text-emerald-700 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {billFile.name} ({(billFile.size/1024).toFixed(1)} KB)
                          <button type="button" onClick={() => { setBillFile(null); setBillDataUrl(''); }} className="ml-1 text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                        </div>
                      )}
                      {!billFile && editingAsset?.has_bill && (
                        <span className="text-xs text-slate-500 italic">Existing bill on file. Choose a new file to replace it.</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-emerald-700 hover:bg-emerald-800" data-testid="save-asset-btn">
                    {editingAsset ? 'Update Asset' : 'Add Asset'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="asset-search-input"
          />
        </div>
        
        {isAdmin() && (
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger className="w-48" data-testid="asset-branch-filter">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="asset-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ASSET_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
          <p className="text-xs text-slate-500 mb-1">Total Assets</p>
          <p className="text-xl font-bold text-slate-900">{summary.totalAssets}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm p-3">
          <p className="text-xs text-emerald-600 mb-1">Total Purchase Value</p>
          <p className="text-lg font-bold text-emerald-900">₹{summary.totalValue.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 shadow-sm p-3">
          <p className="text-xs text-blue-600 mb-1">Current Book Value</p>
          <p className="text-lg font-bold text-blue-900">₹{summary.currentValue.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-orange-50 rounded-lg border border-orange-200 shadow-sm p-3">
          <p className="text-xs text-orange-600 mb-1">Total Depreciation</p>
          <p className="text-lg font-bold text-orange-900">₹{summary.depreciation.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Printable Asset Register */}
      <div id="asset-register-print" className="hidden">
        <h1>WWAGS MIS Portal - Asset Register</h1>
        <h2>{filterBranch !== 'all' ? branches.find(b => b.id === filterBranch)?.name : 'All Branches'}</h2>
        <div className="summary">
          <span className="summary-item"><strong>Total Assets:</strong> {summary.totalAssets}</span>
          <span className="summary-item"><strong>Total Value:</strong> Rs. {summary.totalValue.toLocaleString('en-IN')}</span>
          <span className="summary-item"><strong>Current Value:</strong> Rs. {summary.currentValue.toLocaleString('en-IN')}</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Asset Code</th>
              <th>Asset Name</th>
              <th>Type</th>
              <th>Category</th>
              <th>Qty</th>
              <th>Purchase Date</th>
              <th>Purchase Price</th>
              <th>Life (Years)</th>
              <th>Current Value</th>
              <th>Condition</th>
              <th>Branch</th>
            </tr>
          </thead>
          <tbody>
            {filteredAssets.map(asset => (
              <tr key={asset.id}>
                <td>{asset.asset_code}</td>
                <td>{asset.asset_name}</td>
                <td>{asset.asset_type === 'Other' ? asset.asset_type_other : asset.asset_type}</td>
                <td>{asset.category}</td>
                <td className="text-right">{asset.quantity ?? 1}</td>
                <td>{new Date(asset.purchase_date).toLocaleDateString('en-IN')}</td>
                <td className="text-right">Rs. {lineTotal(asset).toLocaleString('en-IN')}</td>
                <td>{asset.life_expectancy_years}</td>
                <td className="text-right">Rs. {(asset.current_value ?? lineTotal(asset)).toLocaleString('en-IN')}</td>
                <td>{asset.condition}</td>
                <td>{asset.branch_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Asset Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-700 text-white">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Asset Code</th>
                <th className="px-3 py-2 text-left font-semibold">Asset Name</th>
                <th className="px-3 py-2 text-left font-semibold">Type</th>
                <th className="px-3 py-2 text-left font-semibold">Category</th>
                <th className="px-3 py-2 text-center font-semibold">Qty</th>
                <th className="px-3 py-2 text-center font-semibold">Purchase Date</th>
                <th className="px-3 py-2 text-right font-semibold">Purchase Price</th>
                <th className="px-3 py-2 text-center font-semibold">Life</th>
                <th className="px-3 py-2 text-right font-semibold">Current Value</th>
                <th className="px-3 py-2 text-center font-semibold">Condition</th>
                <th className="px-3 py-2 text-left font-semibold">Branch</th>
                <th className="px-3 py-2 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAssets.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-4 py-12 text-center text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No assets found</p>
                  </td>
                </tr>
              ) : (
                filteredAssets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <button onClick={() => openDetail(asset)} className="text-blue-700 hover:underline font-medium" data-testid={`view-asset-${asset.id}`}>
                        {asset.asset_code}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      <button onClick={() => openDetail(asset)} className="text-left hover:text-blue-700 hover:underline">
                        {asset.asset_name}
                      </button>
                      {asset.has_bill && (
                        <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                          <FileText className="w-2.5 h-2.5" /> Bill
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                        {asset.asset_type === 'Other' ? asset.asset_type_other : asset.asset_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{asset.category}</td>
                    <td className="px-3 py-2 text-center font-medium text-slate-800" data-testid={`asset-qty-${asset.id}`}>{asset.quantity ?? 1}</td>
                    <td className="px-3 py-2 text-center text-slate-600">
                      {new Date(asset.purchase_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-3 py-2 text-right font-medium" data-testid={`asset-price-${asset.id}`}>
                      <div className="font-semibold">₹{(asset.total_purchase_value ?? ((asset.purchase_price || 0) * (asset.quantity ?? 1))).toLocaleString('en-IN')}</div>
                      {(asset.quantity ?? 1) > 1 && (
                        <div className="text-[10px] text-slate-500 font-normal">₹{(asset.purchase_price || 0).toLocaleString('en-IN')} × {asset.quantity}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-600">{asset.life_expectancy_years} yrs</td>
                    <td className="px-3 py-2 text-right font-medium text-emerald-700" data-testid={`asset-currval-${asset.id}`}>
                      ₹{(asset.current_value ?? (asset.total_purchase_value ?? ((asset.purchase_price || 0) * (asset.quantity ?? 1)))).toLocaleString('en-IN')}
                      {asset.total_depreciation > 0 && (
                        <div className="text-[10px] text-orange-600 font-normal">−₹{asset.total_depreciation.toLocaleString('en-IN')} dep.</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        asset.condition === 'Good' ? 'bg-emerald-100 text-emerald-800' :
                        asset.condition === 'Fair' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {asset.condition}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{asset.branch_name}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(asset)}
                          className="h-7 w-7 p-0 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          title="View details & bill"
                          data-testid={`view-detail-${asset.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(asset)}
                          className="h-7 w-7 p-0"
                          data-testid={`edit-asset-${asset.id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {isAdminOnly() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(asset.id)}
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-asset-${asset.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* === Asset Detail Dialog (with Bill viewer) === */}
      <AssetDetailDialog
        asset={detailAsset}
        onClose={() => { setDetailAsset(null); setDetailBill(null); }}
        onEdit={(a) => { handleEdit(a); setDetailAsset(null); setDetailBill(null); }}
      />
    </div>
  );
};

export default Assets;
